import React, { useEffect, useRef, useState } from 'react';
import { IonIcon } from '@ionic/react';
import { playOutline } from 'ionicons/icons';
import api from '../../services/api';
import './HoverVideo.css';

const HoverVideo = ({ videoUrl, executionId, events: externalEvents, onCurrentEventChange }) => {
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const currentEventRef = useRef(null);
  const isHoveringRef = useRef(false);
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [error, setError] = useState(false);
  const [progress, setProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [events, setEvents] = useState(externalEvents || []);
  const [eventsFetched, setEventsFetched] = useState(false);
  const [isInViewport, setIsInViewport] = useState(false);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInViewport(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before it comes into view
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Only load thumbnail when in viewport
  useEffect(() => {
    if (!videoUrl || !isInViewport) {
      return;
    }

    if (!videoUrl) {
      setError(true);
      return;
    }

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';
    video.muted = true;

    const handleLoadedMetadata = () => {
      const seekTo = Math.min(1, video.duration - 0.1);
      video.currentTime = seekTo;
    };

    const handleSeeked = () => {
      try {
        const canvas = canvasRef.current;
        if (!canvas) return;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        setThumbnailLoaded(true);
        setError(false);
      } catch (err) {
        console.error('Error capturing frame:', err);
        setError(true);
      }
    };

    const handleError = () => {
      console.error('Error loading video');
      setError(true);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('error', handleError);

    video.src = videoUrl;
    video.load();

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('error', handleError);
      video.src = '';
    };
  }, [videoUrl, isInViewport]);

  const handleTimeUpdate = () => {
    if (!videoRef.current || !videoRef.current.duration) return;
    
    // Always update progress
    const currentProgress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
    setProgress(currentProgress);
    
    // Only update events while hovering (use ref to avoid closure issues)
    if (!isHoveringRef.current || !onCurrentEventChange || !events || events.length === 0) return;
    
    const currentTimeMs = videoRef.current.currentTime * 1000;
    
    // Find the most recent event that has occurred
    let currentEvent = null;
    for (let i = 0; i < events.length; i++) {
      if (events[i].videoOffsetMs !== undefined && events[i].videoOffsetMs <= currentTimeMs) {
        currentEvent = events[i];
      } else {
        break;
      }
    }
    
    // Only call the change handler if the event actually changed
    if (currentEvent !== currentEventRef.current) {
      currentEventRef.current = currentEvent;
      onCurrentEventChange(currentEvent);
    }
  };

  const fetchEvents = async () => {
    if (!executionId || eventsFetched) return;
    
    try {
      const data = await api.get(`/executions/${executionId}`);
      if (data.urls?.events) {
        const eventsResponse = await fetch(data.urls.events);
        if (eventsResponse.ok) {
          const eventsData = await eventsResponse.json();
          const actionEvents = eventsData
            .filter(e => e.type === 'action' || e.videoOffsetMs !== undefined)
            .sort((a, b) => (a.videoOffsetMs || 0) - (b.videoOffsetMs || 0));
          
          setEvents(actionEvents);
          setEventsFetched(true);
        }
      }
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  };

  const handleMouseEnter = async () => {
    setIsHovering(true);
    isHoveringRef.current = true;
    setProgress(0);
    
    await fetchEvents();
    
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.addEventListener('timeupdate', handleTimeUpdate);
      videoRef.current.play().catch(err => {
        console.log('Video play failed:', err);
      });
      
      if (videoRef.current.duration && !videoDuration) {
        setVideoDuration(videoRef.current.duration);
      }
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    isHoveringRef.current = false;
    setProgress(0);
    
    // IMPORTANT: Remove listener and reset state BEFORE resetting video time
    // Otherwise resetting currentTime = 0 triggers a final timeupdate that finds the first event
    if (videoRef.current) {
      videoRef.current.removeEventListener('timeupdate', handleTimeUpdate);
      videoRef.current.pause();
    }
    
    currentEventRef.current = null;
    if (onCurrentEventChange) {
      onCurrentEventChange(null);
    }
    
    // Reset video time AFTER everything else
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  if (error) {
    return (
      <div className="hover-video-placeholder">
        <IonIcon icon={playOutline} />
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="hover-video-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {!thumbnailLoaded && !error && (
        <div className="hover-video-skeleton" />
      )}
      <canvas 
        ref={canvasRef} 
        className="hover-video-canvas"
        style={{ display: isHovering ? 'none' : 'block' }}
      />
      {thumbnailLoaded && (
        <div 
          className="hover-video-overlay"
          style={{ opacity: isHovering ? 0 : 1 }}
        />
      )}
      <video
        ref={videoRef}
        className="hover-video-element"
        src={isInViewport ? videoUrl : ''}
        muted
        loop
        playsInline
        preload="none"
        onLoadedMetadata={(e) => {
          if (e.target.duration) {
            setVideoDuration(e.target.duration);
          }
        }}
        style={{ display: isHovering && thumbnailLoaded ? 'block' : 'none' }}
      />
      {isHovering && (
        <div className="hover-video-progress-bar">
          <div 
            className="hover-video-progress-fill"
            style={{ width: `${progress}%` }}
          />
          {events.length > 0 && videoDuration > 0 && events
            .filter(e => e.videoOffsetMs !== undefined)
            .map((event, idx) => {
              const eventPositionPercent = (event.videoOffsetMs / (videoDuration * 1000)) * 100;
              return (
                <div
                  key={idx}
                  className="hover-video-event-marker"
                  style={{ left: `${eventPositionPercent}%` }}
                  title={event.data?.tool || event.tool || 'Event'}
                />
              );
            })}
        </div>
      )}
    </div>
  );
};

export default HoverVideo;

